/**
 * Google Analytics 4 Event Tracking Utilities
 * Provides functions to track conversion events across the application
 */

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

/**
 * Track a custom event in Google Analytics
 * @param eventName - The name of the event
 * @param eventParams - Additional parameters for the event
 */
export function trackEvent(
  eventName: string,
  eventParams: Record<string, any> = {}
): void {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

/**
 * Track quote request submission
 * @param quoteData - Data about the quote request
 */
export function trackQuoteRequest(quoteData: {
  serviceType?: string;
  cargoType?: string;
  origin?: string;
  destination?: string;
  value?: string;
}): void {
  trackEvent('quote_request', {
    event_category: 'conversion',
    event_label: 'Quote Wizard Submission',
    service_type: quoteData.serviceType || 'unknown',
    cargo_type: quoteData.cargoType || 'unknown',
    origin: quoteData.origin || 'unknown',
    destination: quoteData.destination || 'unknown',
    cargo_value: quoteData.value || 'unknown',
  });
}

/**
 * Track form submission
 * @param formType - Type of form submitted
 * @param formData - Additional form data
 */
export function trackFormSubmission(
  formType: string,
  formData: Record<string, any> = {}
): void {
  trackEvent('form_submission', {
    event_category: 'conversion',
    event_label: `${formType} Form`,
    form_type: formType,
    ...formData,
  });
}

/**
 * Track service inquiry
 * @param inquiryType - Type of inquiry (phone, email, whatsapp, emergency)
 * @param contactMethod - Contact method used
 */
export function trackServiceInquiry(
  inquiryType: string,
  contactMethod: string
): void {
  trackEvent('service_inquiry', {
    event_category: 'conversion',
    event_label: `${inquiryType} Contact`,
    inquiry_type: inquiryType,
    contact_method: contactMethod,
  });
}

/**
 * Track CTA button click
 * @param ctaLabel - Label of the CTA button
 * @param ctaLocation - Location/page where CTA was clicked
 * @param ctaDestination - Destination URL or action
 */
export function trackCTAClick(
  ctaLabel: string,
  ctaLocation: string,
  ctaDestination?: string
): void {
  trackEvent('cta_click', {
    event_category: 'conversion',
    event_label: ctaLabel,
    cta_location: ctaLocation,
    cta_destination: ctaDestination || 'unknown',
  });
}

/**
 * Track service selection
 * @param serviceName - Name of the service selected
 * @param serviceCategory - Category of the service
 */
export function trackServiceSelection(
  serviceName: string,
  serviceCategory?: string
): void {
  trackEvent('service_selection', {
    event_category: 'engagement',
    event_label: serviceName,
    service_name: serviceName,
    service_category: serviceCategory || 'unknown',
  });
}
